import { atom } from "jotai"
import { store } from "../store"
import { projectAtom } from "./projects-state"

type ProjectMode = "editing" | "culling"

interface ProjectSessionTiming {
	activeMode: ProjectMode | null
	activeModeStartedAt: number | null
	cullingTimeSpentMs: number
	editingTimeSpentMs: number
}

const projectSessionTimingAtom = atom<Record<string, ProjectSessionTiming>>({})

const getProjectId = (projectId?: string | null) => {
	return projectId ?? store.get(projectAtom)?.id ?? null
}

export const markProjectModeEntered = (mode: ProjectMode, projectId?: string | null) => {
	const resolvedProjectId = getProjectId(projectId)
	if (!resolvedProjectId) {
		return
	}

	const now = Date.now()
	store.set(projectSessionTimingAtom, (prev) => {
		const current = prev[resolvedProjectId] ?? {
			activeMode: null,
			activeModeStartedAt: null,
			cullingTimeSpentMs: 0,
			editingTimeSpentMs: 0,
		}

		if (current.activeMode === mode && current.activeModeStartedAt != null) {
			return prev
		}

		let cullingTimeSpentMs = current.cullingTimeSpentMs
		let editingTimeSpentMs = current.editingTimeSpentMs

		if (current.activeMode && current.activeModeStartedAt != null) {
			const elapsedMs = Math.max(0, now - current.activeModeStartedAt)
			if (current.activeMode === "culling") {
				cullingTimeSpentMs += elapsedMs
			} else {
				editingTimeSpentMs += elapsedMs
			}
		}

		return {
			...prev,
			[resolvedProjectId]: {
				activeMode: mode,
				activeModeStartedAt: now,
				cullingTimeSpentMs,
				editingTimeSpentMs,
			},
		}
	})
}

export const getProjectModeTimeSpentMinutes = (projectId?: string | null) => {
	const resolvedProjectId = getProjectId(projectId)
	const timing = resolvedProjectId ? store.get(projectSessionTimingAtom)[resolvedProjectId] : null

	if (!timing) {
		return {
			cullingTimeSpentMinutes: 0,
			editingTimeSpentMinutes: 0,
		}
	}

	const now = Date.now()

	let cullingTimeSpentMs = timing.cullingTimeSpentMs
	let editingTimeSpentMs = timing.editingTimeSpentMs

	if (timing.activeMode && timing.activeModeStartedAt != null) {
		const elapsedMs = Math.max(0, now - timing.activeModeStartedAt)
		if (timing.activeMode === "culling") {
			cullingTimeSpentMs += elapsedMs
		} else {
			editingTimeSpentMs += elapsedMs
		}
	}

	const cullingTimeSpentMinutes = Math.floor(cullingTimeSpentMs / 60000)
	const editingTimeSpentMinutes = Math.floor(editingTimeSpentMs / 60000)

	return {
		cullingTimeSpentMinutes,
		editingTimeSpentMinutes,
	}
}

export const clearProjectModeTiming = (projectId?: string | null) => {
	const resolvedProjectId = getProjectId(projectId)
	if (!resolvedProjectId) {
		return
	}

	store.set(projectSessionTimingAtom, (prev) => {
		if (!prev[resolvedProjectId]) {
			return prev
		}

		const updated = { ...prev }
		delete updated[resolvedProjectId]
		return updated
	})
}
